import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CalendarClock, Heart, Star } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  type ConsentEvent,
  type Customer360,
  type CustomerDetail as Detail,
  type Feedback,
  type LoyaltyDetail,
  apiError,
} from './lib';
import IdentityTab from './customer/IdentityTab';
import ConsentTab from './customer/ConsentTab';

type PointsAction = 'earn' | 'redeem' | 'adjust';

const TABS = ['overview', 'orders', 'reservations', 'identity', 'consent', 'feedback', 'notes'] as const;
type Tab = (typeof TABS)[number];

/**
 * English fallbacks for keys `@/lib/i18n` does not carry yet. There
 * is no local resource bundle, so without these the tab strip renders its own
 * key names. They are pending in **Arabic too**: an Arabic screen reads English
 * here until they are added.
 */
/**
 * One customer, as a profile.
 *
 * ## Still a props component, still one mount point
 *
 * `{ customerId, onChanged }` from `CustomersTab`, which owns the list this is
 * the right-hand pane of and re-reads it when a write here lands. That callback
 * is the whole reason this is not a route: a route would have to refetch the
 * list itself, and the two copies could then disagree. Grep confirms this is the
 * only place it is mounted, so the tab strip below is internal state and not a
 * URL — nothing else can deep-link into a tab that does not exist.
 *
 * ## Four things on the frame with nothing behind them
 *
 * - **`Gold member`, `Platinum at 5,000`, `1.2x multiplier`.**
 *   `LoyaltyAccount.tier` is a nullable string that nothing in the backend reads
 *   or writes; there are no thresholds and no earn multiplier. The pill would
 *   have replaced a real derived label — `CustomerSegment`, computed by
 *   `segmentOf()` — with invention, so the segment stays and the tier is not
 *   rendered even when the API carries one.
 * - **`Birthday`, `Favourite table`, `Dietary restrictions`, `Preferred visit
 *   time`.** `Customer` is `{name, phone, email, locale, notes}` and
 *   `UpdateCustomerDto` matches it exactly, so under `forbidNonWhitelisted` any
 *   one of these 400s the whole save. `notes` is documented as *"allergies,
 *   seating preference, anything the floor needs"* — it is where these already
 *   live, as prose — so the Notes tab is that one field, labelled as one field.
 *   A dietary restriction in particular is safety-relevant: a box that looks
 *   like it stores an allergy and does not is worse than no box.
 * - **`Monthly Spending Trend`.** Nothing aggregates a customer's spend by
 *   month. `GET /customers/:id` returns totals and an order list with no time
 *   series, and the analytics layer has no `customer` dimension and no month
 *   bucket — only `date`, `hour` and `dayOfWeek` — so `POST /analytics/query` is
 *   not a way round it. Drawing the bars off the last few orders would be a
 *   chart of five rows pretending to be a trend.
 * - **`Block customer`.** No `isBlocked` anywhere in either repo, and no route.
 *   `Send message` is dropped for a different reason: `POST /notifications`
 *   exists but is gated on `settings.update`, which is not a permission a
 *   customers screen should be assuming.
 *
 * ## And what the frame would have deleted
 *
 * The whole loyalty mutation surface (earn / redeem / adjust, with a reason
 * required on a manual correction), the ledger, the drift banner that flags a
 * stored balance disagreeing with its own append-only ledger, visit history,
 * favourite products and their feedback. All of it works today; the frame draws
 * loyalty as a read-only bar. It is redistributed across the tabs, not removed.
 * The reservations tab is the one genuine addition: `GET /customers/:id` has
 * been returning `reservations` all along and nothing rendered them.
 */
export default function CustomerDetail({
  customerId,
  onChanged,
}: {
  customerId: string;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canUpdate = can('customers.update');
  // A customer's history spans branches and `/customers/:id` returns no
  // per-record currency, so these figures are read in the tenant's base
  // currency. (A tenant trading in two currencies needs the API to state one
  // per order before this can be exact.)
  const { format: formatMoney } = useCurrency(null);

  const [detail, setDetail] = useState<Detail | null>(null);
  const [loyalty, setLoyalty] = useState<LoyaltyDetail | null>(null);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  // The 360 view (ADR-0010) — identity across merges, per-channel consent,
  // tags and dynamic segment membership. `GET /customers/:id` above is left
  // exactly as it was for the fields this screen already used; this is
  // additive, so a tenant where the CRM crm module 404s still shows overview,
  // orders, reservations, feedback and notes.
  const [profile360, setProfile360] = useState<Customer360 | null>(null);
  const [profile360Error, setProfile360Error] = useState<string | null>(null);
  const [consentHistory, setConsentHistory] = useState<ConsentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>('overview');
  const [action, setAction] = useState<PointsAction>('earn');
  const [points, setPoints] = useState('100');
  const [reason, setReason] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<Detail>(`/customers/${customerId}`),
      api.get<LoyaltyDetail>(`/loyalty/accounts/${customerId}`).catch(() => ({ data: null })),
      api
        .get<{ data: Feedback[] }>('/feedback', { params: { customerId, limit: 25 } })
        .catch(() => ({ data: { data: [] as Feedback[] } })),
    ])
      .then(([profile, account, notes]) => {
        setDetail(profile.data);
        setLoyalty(account.data);
        setFeedback(notes.data.data ?? []);
        setNoteDraft(profile.data?.notes ?? '');
      })
      .catch((e) => setError(apiError(e, t('crm.customers.loadFailed'))))
      .finally(() => setLoading(false));

    // Gated on `customers.view`, same as the read above, so this never turns a
    // permission gap into a screen-level error — a role that cannot even reach
    // this component would never have this promise construct.
    setProfile360Error(null);
    Promise.all([
      api.get<Customer360>(`/customers/${customerId}/360`),
      api
        .get<ConsentEvent[]>(`/customers/${customerId}/consent/history`)
        .catch(() => ({ data: [] as ConsentEvent[] })),
    ])
      .then(([profile, history]) => {
        setProfile360(profile.data);
        setConsentHistory(history.data ?? []);
      })
      .catch((e) => setProfile360Error(apiError(e, t('crm.identity.loadFailed'))));
  }, [customerId, t]);

  useEffect(load, [load]);

  const stats = useMemo(
    () =>
      detail
        ? [
            { key: 'visits', label: t('crm.customers.visits'), value: String(detail.visits) },
            {
              key: 'totalSpend',
              label: t('crm.customers.totalSpend'),
              value: formatMoney(detail.totalSpend),
            },
            {
              key: 'averageSpend',
              label: t('crm.customers.averageSpend'),
              value: formatMoney(detail.averageSpend),
            },
            {
              key: 'lastVisit',
              label: t('crm.customers.lastVisit'),
              value: detail.lastVisitAt
                ? formatDateTime(detail.lastVisitAt, i18n.language)
                : '—',
            },
          ]
        : [],
    [detail, formatMoney, i18n.language, t],
  );

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!detail) return <EmptyState title={t('crm.customers.notFound')} />;

  const submitPoints = async () => {
    const value = Number(points) || 0;
    try {
      if (action === 'earn') {
        await api.post(`/loyalty/accounts/${customerId}/earn`, {
          points: Math.abs(value),
          reason: reason || undefined,
        });
      } else if (action === 'redeem') {
        await api.post(`/loyalty/accounts/${customerId}/redeem`, {
          points: Math.abs(value),
          reason: reason || undefined,
        });
      } else {
        await api.post(`/loyalty/accounts/${customerId}/adjust`, { points: value, reason });
      }
      toast.success(t('crm.loyalty.done'));
      setReason('');
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.loyalty.failed')));
    }
  };

  /**
   * `UpdateCustomerDto = PartialType(CreateCustomerDto)` and declares exactly
   * `name, phone, email, locale, notes`. Only `notes` is sent, and nothing is
   * added beside it — a `birthday` here would 400 the request, not be ignored.
   */
  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.patch(`/customers/${customerId}`, { notes: noteDraft });
      toast.success(t('crm.customers.saved'));
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.customers.saveFailed')));
    } finally {
      setSavingNote(false);
    }
  };

  const drift = loyalty ? loyalty.ledgerBalance !== loyalty.points : false;
  const noteChanged = noteDraft !== (detail.notes ?? '');

  return (
    <div className="space-y-4">
      {/* ---------------------------------------------------- identity row */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar name={detail.name} size="lg" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-semibold">
                  {detail.name ?? t('crm.customers.unnamed')}
                </h2>
                {/* Derived server-side from behaviour, never stored, so it
                    cannot go stale — and not a tier. */}
                <Badge tone={detail.segment === 'VIP' ? 'brand' : 'neutral'}>
                  {t(`crm.segment.${detail.segment}`)}
                </Badge>
              </div>
              <p className="text-xs text-ink-muted">
                {t('crm.customers.customerSince', 'Customer since {{date}}', {
                  date: formatDateTime(detail.createdAt, i18n.language),
                })}
              </p>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.key}>
                <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{stat.label}</dt>
                <dd className="text-lg font-semibold">{stat.value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------- tabs */}
      <nav className="scrollbar-thin flex gap-2 overflow-x-auto border-b pb-px">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'whitespace-nowrap rounded-t-lg border-b-2 px-3 py-2 text-sm transition-colors',
              tab === key
                ? 'border-accent font-medium text-accent'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {t(`crm.customers.tabs.${key}`)}
          </button>
        ))}
      </nav>

      {/* -------------------------------------------------------- overview */}
      {tab === 'overview' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          {/* `min-w-0` on both tracks — the order table scrolls inside its own
              wrapper rather than widening the page on a phone. */}
          <div className="min-w-0 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('crm.customers.orderHistory')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {detail.orderHistory.length === 0 ? (
                  <EmptyState title={t('crm.customers.noOrders')} />
                ) : (
                  <ul className="divide-y text-sm">
                    {detail.orderHistory.slice(0, 5).map((order) => (
                      <li
                        key={order.id}
                        className="flex flex-wrap items-center justify-between gap-3 p-3"
                      >
                        <div>
                          <p className="font-medium">
                            {formatDateTime(order.placedAt ?? order.createdAt, i18n.language)}
                          </p>
                          <p className="text-xs text-ink-muted">{order.number}</p>
                        </div>
                        <span className="tabular-nums">{formatMoney(order.total)}</span>
                        {/* Through the exhaustive table, never hand-coloured. */}
                        <Badge tone={toneForStatus(order.status)}>{order.status}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="min-w-0 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('crm.customers.contact')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {/* Two rows, not three: there is no birthday column. */}
                {(
                  [
                    [t('crm.customers.phone'), detail.phone],
                    [t('crm.customers.email'), detail.email],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{label}</p>
                    <p dir={value?.startsWith('+') ? 'ltr' : undefined}>{value || '—'}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('crm.customers.notes')}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {detail.notes ? (
                  <p className="whitespace-pre-wrap">{detail.notes}</p>
                ) : (
                  <p className="text-ink-muted">{t('crm.customers.notesHint')}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle>{t('crm.loyalty.title')}</CardTitle>
                  <p className="text-xs text-ink-muted">{t('crm.loyalty.subtitle')}</p>
                </div>
                <div className="text-end">
                  <p className="text-2xl font-semibold">{loyalty?.points ?? 0}</p>
                  <p className="text-xs text-ink-muted">{t('crm.loyalty.points')}</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {drift && (
                  <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                    {t('crm.loyalty.drift', {
                      stored: loyalty?.points ?? 0,
                      ledger: loyalty?.ledgerBalance ?? 0,
                    })}
                  </p>
                )}

                {canUpdate && (
                  <div className="space-y-2 rounded-lg border p-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Field label={t('crm.loyalty.action')}>
                        <Select
                          value={action}
                          onChange={(e) => setAction(e.target.value as PointsAction)}
                        >
                          <option value="earn">{t('crm.loyalty.earn')}</option>
                          <option value="redeem">{t('crm.loyalty.redeem')}</option>
                          <option value="adjust">{t('crm.loyalty.adjust')}</option>
                        </Select>
                      </Field>
                      <Field
                        label={t('crm.loyalty.points')}
                        hint={action === 'adjust' ? t('crm.loyalty.signedHint') : undefined}
                      >
                        <Input
                          type="number"
                          value={points}
                          onChange={(e) => setPoints(e.target.value)}
                        />
                      </Field>
                    </div>
                    {/* Required on a manual correction, because an unexplained
                        entry in an append-only ledger is unauditable. */}
                    <Field
                      label={t('crm.loyalty.reason')}
                      required={action === 'adjust'}
                      hint={action === 'adjust' ? t('crm.loyalty.reasonRequired') : undefined}
                    >
                      <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <Button
                      className="w-full"
                      onClick={() => void submitPoints()}
                      disabled={action === 'adjust' && !reason.trim()}
                    >
                      {t('common.confirm')}
                    </Button>
                  </div>
                )}

                {(loyalty?.recentLedger ?? []).length === 0 ? (
                  <p className="text-sm text-ink-muted">{t('crm.loyalty.noLedger')}</p>
                ) : (
                  <ul className="divide-y rounded-lg border text-sm">
                    {loyalty?.recentLedger.map((entry) => (
                      <li
                        key={entry.id}
                        className="flex items-center justify-between gap-3 p-2.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate">{entry.reason}</p>
                          <p className="text-xs text-ink-muted">
                            {formatDateTime(entry.createdAt, i18n.language)}
                            {entry.refType ? ` · ${entry.refType}` : ''}
                          </p>
                        </div>
                        <span
                          className={
                            entry.points >= 0
                              ? 'text-green-700 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }
                        >
                          {entry.points >= 0 ? '+' : ''}
                          {entry.points}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon as={Heart} className="text-accent" />
                  {t('crm.customers.favourites')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {detail.favouriteProducts.length === 0 ? (
                  <EmptyState title={t('crm.customers.noFavourites')} />
                ) : (
                  <ul className="divide-y text-sm">
                    {detail.favouriteProducts.map((favourite) => (
                      <li
                        key={favourite.itemId}
                        className="flex items-center justify-between gap-3 p-3"
                      >
                        <span>{favourite.name ?? favourite.itemId}</span>
                        <span className="text-ink-muted">
                          {t('crm.customers.orderedTimes', {
                            count: favourite.timesOrdered,
                            quantity: favourite.totalQuantity,
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------- orders */}
      {tab === 'orders' && (
        <Card>
          <CardContent className="p-0">
            {detail.orderHistory.length === 0 ? (
              <EmptyState title={t('crm.customers.noOrders')} />
            ) : (
              <TableWrap className="rounded-none border-x-0 border-t-0">
                <Table>
                  <THead>
                    <tr>
                      <TH>{t('crm.customers.orderNumber')}</TH>
                      <TH>{t('crm.customers.fulfillment')}</TH>
                      <TH>{t('crm.customers.orderStatus')}</TH>
                      <TH>{t('common.total')}</TH>
                      <TH>{t('crm.customers.placedAt')}</TH>
                    </tr>
                  </THead>
                  <tbody>
                    {detail.orderHistory.map((order) => (
                      <TR key={order.id}>
                        <TD>{order.number}</TD>
                        <TD>
                          {t(`order.fulfillment.${order.fulfillmentType}`, order.fulfillmentType)}
                        </TD>
                        <TD>
                          <Badge tone={toneForStatus(order.status)}>{order.status}</Badge>
                        </TD>
                        <TD>{formatMoney(order.total)}</TD>
                        <TD className="text-ink-muted">
                          {formatDateTime(order.placedAt ?? order.createdAt, i18n.language)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---------------------------------------------------- reservations */}
      {tab === 'reservations' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icon as={CalendarClock} className="text-accent" />
              {t('crm.customers.tabs.reservations')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {detail.reservations.length === 0 ? (
              <EmptyState title={t('crm.customers.noReservations')} />
            ) : (
              <ul className="divide-y text-sm">
                {detail.reservations.map((reservation) => (
                  <li
                    key={reservation.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-3"
                  >
                    <div>
                      <p className="font-medium">
                        {formatDateTime(reservation.startsAt, i18n.language)}
                      </p>
                      {reservation.partySize !== null && (
                        <p className="text-xs text-ink-muted">
                          {t('crm.customers.headCount', { count: reservation.partySize })}
                        </p>
                      )}
                    </div>
                    <Badge tone={toneForStatus(reservation.status)}>{reservation.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* ------------------------------------------------ identity & merge */}
      {tab === 'identity' &&
        (profile360 ? (
          <IdentityTab customerId={customerId} profile={profile360} onChanged={load} />
        ) : profile360Error ? (
          <ErrorState message={profile360Error} onRetry={load} />
        ) : (
          <LoadingState label={t('common.loading')} />
        ))}

      {/* --------------------------------------------------------- consent */}
      {tab === 'consent' &&
        (profile360 ? (
          <ConsentTab
            customerId={customerId}
            consent={profile360.consent}
            history={consentHistory}
            onChanged={load}
          />
        ) : profile360Error ? (
          <ErrorState message={profile360Error} onRetry={load} />
        ) : (
          <LoadingState label={t('common.loading')} />
        ))}

      {/* -------------------------------------------------------- feedback */}
      {tab === 'feedback' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icon as={Star} className="text-amber-500" />
              {t('crm.customers.theirFeedback')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {feedback.length === 0 ? (
              <EmptyState title={t('crm.feedback.empty')} />
            ) : (
              <ul className="divide-y text-sm">
                {feedback.map((entry) => (
                  <li key={entry.id} className="space-y-1 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">
                        {t('crm.feedback.ratingOf', { rating: entry.rating })}
                      </span>
                      <Badge tone={toneForStatus(entry.status)}>
                        {t(`crm.feedbackStatus.${entry.status}`)}
                      </Badge>
                    </div>
                    {entry.comment && <p>{entry.comment}</p>}
                    <p className="text-xs text-ink-muted">
                      {formatDateTime(entry.createdAt, i18n.language)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* ----------------------------------------------------------- notes */}
      {tab === 'notes' && (
        <Card>
          <CardContent className="space-y-3 p-5">
            <Field
              label={t('crm.customers.notes')}
              hint={t('crm.customers.notesOneFieldHint')}
            >
              <Textarea
                rows={8}
                value={noteDraft}
                disabled={!canUpdate}
                onChange={(e) => setNoteDraft(e.target.value)}
              />
            </Field>
            {canUpdate && (
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  disabled={!noteChanged || savingNote}
                  onClick={() => setNoteDraft(detail.notes ?? '')}
                >
                  {t('common.cancel')}
                </Button>
                <Button onClick={() => void saveNote()} disabled={!noteChanged || savingNote}>
                  {t('common.save')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
